import { AUFWAENDE, type Aufwand, type KiModell, type KiModellwahl } from '../api/ki';

/** Was die Maske von der Liste braucht — auch dann, wenn schon die Abfrage beim Server scheiterte. */
export type Modellliste = Pick<KiModellwahl, 'modelle' | 'grund'>;

export type Listenpreis = { readonly eingabe: number; readonly ausgabe: number };

/** Woher die Listenpreise stammen — steht auch im Hinweis der Maske. */
export const LISTENPREISE_QUELLE =
  'Preisseite von Anthropic, platform.claude.com/docs/en/about-claude/pricing, abgerufen am 04.10.2026';

/**
 * Listenpreise in US-$ je Million Token, Eingabe und Ausgabe (KI-08) — nur
 * ein VORSCHLAG beim Wechsel des Modells.
 *
 * Quelle: die amtliche Preisseite von Anthropic,
 * platform.claude.com/docs/en/about-claude/pricing, abgerufen am 04.10.2026.
 * Haiku 4.5 und Opus 4.5 fehlen mit Absicht: Ihnen fehlt laut Models-API das
 * adaptive Denken, der Modellzugang kann sie nicht bedienen, und im Dropdown
 * stehen sie nicht.
 *
 * Zugeordnet wird über den Namen, den die Models-API nennt („Claude Opus
 * 5.5" → „Opus 5.5"), nicht über die Kennung: Kennungen werden nicht
 * geraten. Ein Modell, das hier fehlt, bekommt keinen Vorschlag; dann trägt
 * die Verwaltung die Preise laut Preisliste ein. Was gilt, ist die Rechnung
 * des Anbieters — die Felder bleiben deshalb in jedem Fall änderbar.
 */
const LISTENPREISE: ReadonlyMap<string, Listenpreis> = new Map([
  ['Sonnet 5.5', { eingabe: 2, ausgabe: 10 }],
  // Seit dem 01.09.2026 der Standardpreis.
  ['Sonnet 5', { eingabe: 2, ausgabe: 10 }],
  ['Opus 5.5', { eingabe: 4, ausgabe: 20 }],
  ['Opus 5', { eingabe: 5, ausgabe: 25 }],
  ['Opus 4.8', { eingabe: 5, ausgabe: 25 }],
  ['Opus 4.7', { eingabe: 5, ausgabe: 25 }],
  ['Opus 4.6', { eingabe: 5, ausgabe: 25 }],
  ['Fable 5.1', { eingabe: 10, ausgabe: 50 }],
  ['Fable 5', { eingabe: 10, ausgabe: 50 }],
  ['Sonnet 4.6', { eingabe: 3, ausgabe: 15 }],
]);

export function listenpreis(name: string): Listenpreis | undefined {
  return LISTENPREISE.get(name.replace(/^Claude\s+/, '').trim());
}

const FAEHIGKEIT: Record<KiModell['fehlt'][number], string> = {
  strukturierte_ausgabe: 'strukturierte Ausgabe',
  adaptives_denken: 'adaptives Denken',
  aufwand: 'Aufwandsstufen',
};

export const fehltText = (modell: KiModell): string => modell.fehlt.map((f) => FAEHIGKEIT[f]).join(', ');

export type Modelloption = { readonly kennung: string; readonly beschriftung: string };

/**
 * Das Dropdown „Modell" (KI-21): die Modelle, die der Modellzugang bedienen
 * kann, das neueste oben — und das eingestellte, immer, auch wenn die Liste
 * es nicht (mehr) kennt. Dann steht der Vermerk in der Beschriftung.
 */
export function modelloptionen(liste: Modellliste | undefined, eingestellt: string): readonly Modelloption[] {
  const modelle = liste?.modelle ?? [];
  const optionen = modelle
    .filter((m) => m.bedienbar === true)
    .map((m) => ({ kennung: m.kennung, beschriftung: `${m.name} · ${m.kennung}` }));
  if (optionen.some((o) => o.kennung === eingestellt)) return optionen;

  const bekannt = modelle.find((m) => m.kennung === eingestellt);
  const vermerk =
    liste === undefined || liste.grund !== null
      ? ''
      : bekannt === undefined
        ? ' — nicht in der Liste des Anbieters'
        : bekannt.bedienbar === null
          ? ' — Fähigkeiten vom Anbieter nicht genannt'
          : ' — kann der Modellzugang nicht bedienen';
  const name = bekannt === undefined ? '' : `${bekannt.name} · `;
  return [...optionen, { kennung: eingestellt, beschriftung: `${name}${eingestellt}${vermerk}` }];
}

/** Die Stufen des Modells; kennt die Liste es nicht, alle — dann weiß es niemand besser. */
export function stufenFuer(modell: KiModell | undefined): readonly Aufwand[] {
  return modell === undefined || modell.aufwaende.length === 0 ? AUFWAENDE : AUFWAENDE.filter((a) => modell.aufwaende.includes(a));
}

/** Die bisherige Stufe, wenn das Modell sie kann — sonst die nächstniedrigere, sonst seine niedrigste. */
export function aufwandFuer(stufen: readonly Aufwand[], bisher: Aufwand): Aufwand {
  if (stufen.includes(bisher)) return bisher;
  const rang = AUFWAENDE.indexOf(bisher);
  return stufen.filter((s) => AUFWAENDE.indexOf(s) < rang).at(-1) ?? stufen[0] ?? bisher;
}
